<template>
  <!-- 剧集抽屉 -->
  <a-drawer
    :title="data && data.remark_name"
    width="1200"
    :closable="false"
    :visible="visible"
    @close="onClose"
  >
    <div class="tool-view">
      <div style="flex: 1;"></div>
      <a-button style="margin-right: 12px;" @click="touchSyncEqVideo" :disabled="isLoading || isSyncAllLoading">
        同步管理中心剧集
      </a-button>
      <a-button style="margin-right: 12px;" @click="touchWxMediaAuditStatus" :disabled="isLoading || isSyncAllLoading">
        微小刷新审核状态
      </a-button>
      <a-button @click="touchWxGetVideoStatus" :disabled="isLoading || isSyncAllLoading">
        微信刷新上传状态
      </a-button>
    </div>
    <TableCheckbox
      sourceKey="id"
      :dataSource="dataSource"
      :selectedRowKeys="selectedRowKeys"
      :selectedRows="selectedRows"
      :pagination="pagination"
      :isLoading="isSyncAllLoading || isLoading"
    >
      <template #checkbox>
        <a-button class="checkbox-btn" @click="touchWxVideoUploadBatch">同步至微小</a-button>
      </template>
      <a-table
        class="table-view"
        :data-source="dataSource"
        :columns="columns"
        :row-key="record => record.id"
        :pagination="pagination"
        :loading="isLoading"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :scroll="{ x: scrollX, y: 'max-content' }"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <div style="max-width: 90px;">
              {{ record.name || '未上传' }}
            </div>
          </template>
          <template v-else-if="column.key === 'wx_upload_status'">
            <span style="white-space: nowrap;">
              {{ (WxVideoUploadStatus.find(item => item.value === record[column.key]) || {}).text || '-' }}
            </span>
          </template>
          <template v-else-if="column.key === 'wx_audit_status'">
            <span style="white-space: nowrap;">
              {{ (WxAuditStatus.find(item => item.value === record[column.key]) || {}).text || '-' }}
              <a-tooltip v-if="record.wx_audit_msg">
                <template #title>{{ record.wx_audit_msg }}</template>
                <InfoCircleOutlined />
              </a-tooltip>
            </span>
          </template>
          <template v-else-if="['visit_count', 'play_count', 'play_over_count', 'unlock_count'].includes(column.key)">
            {{ record[column.key] || 0 }}
          </template>
          <template v-else-if="column.key === 'operation'">
            <a-button type="link" :disabled="!record.preview_url" @click="touchPreview(record)">预览</a-button>
            <a-button type="link" :disabled="!record.video_id" @click="touchIntroduction(record)">剧集简介</a-button>
            <a-button type="link" @click="touchSyncWx(record)">同步至微小</a-button>
          </template>
          <template v-else>
            {{ record[column.key] ?? '-' }}
          </template>
        </template>
      </a-table>
    </TableCheckbox>
    <PreviewVideoProModal ref="RefPreviewModal"></PreviewVideoProModal>
    <Introduction ref="RefIntroduction" @success="getList"></Introduction>
  </a-drawer>
</template>

<script setup>
import { ref, reactive, defineExpose, onBeforeMount } from 'vue'
import { message } from 'ant-design-vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import Introduction from './Introduction'
import TableCheckbox from '@/components/TableCheckbox'
import PreviewVideoProModal from '@/components/preview/PreviewVideoProModal'
import { WxVideoUploadStatus, WxAuditStatus } from '@/utils/constant'
import { getDramaSeriesList, wxVideoUpload, wxGetVideoStatus, wxMediaAuditStatus, syncEqVideo } from '@/api/operate'

let RefIntroduction = ref(null)
let RefPreviewModal = ref(null)
let visible = ref(false)
let data = ref({})
let isLoading = ref(false)
let isSyncAllLoading = ref(false)
let dataSource = ref([])
let selectedRowKeys = ref([])
let selectedRows = ref([])
let scrollX = ref(0)
let pagination = reactive({
  size: 'small',
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true
})
let columns = ref([
  {
    title: '集数',
    dataIndex: 'eq_number',
    key: 'eq_number',
    fixed: 'left',
    width: 70
  },
  {
    title: '文件名',
    dataIndex: 'name',
    key: 'name',
    fixed: 'left',
    width: 90
  },
  {
    title: '微小ID',
    dataIndex: 'wechat_media_id',
    key: 'wechat_media_id',
    width: 120
  },
  {
    title: '微小上传状态',
    dataIndex: 'wx_upload_status',
    key: 'wx_upload_status',
    width: 120
  },
  {
    title: '微小审核状态',
    dataIndex: 'wx_audit_status',
    key: 'wx_audit_status',
    width: 120
  },
  {
    title: '收费',
    dataIndex: 'is_fee',
    key: 'is_fee',
    width: 80
  },
  {
    title: '访问',
    dataIndex: 'visit_count',
    key: 'visit_count',
    width: 80
  },
  {
    title: '播放',
    dataIndex: 'play_count',
    key: 'play_count',
    width: 80
  },
  {
    title: '播放完成',
    dataIndex: 'play_over_count',
    key: 'play_over_count',
    width: 100
  },
  {
    title: '非会员解锁',
    dataIndex: 'unlock_count',
    key: 'unlock_count',
    width: 110
  },
  {
    title: '操作',
    dataIndex: 'operation',
    key: 'operation',
    fixed: 'right',
    width: 240
  }
])

onBeforeMount(() => {
  getScrollX()
})

function getScrollX () {
  let x = 0
  columns.value.forEach(item => {
    x += item.width || 0
  })
  scrollX.value = x
}

function onSelectChange (rowKeys, rows) {
  selectedRowKeys.value = rowKeys
  selectedRows.value = rows
}

function getSelectedVideoRows () {
  if (selectedRows.value.length) {
    return selectedRows.value
  }
  return selectedRowKeys.value
    .map((key) => dataSource.value.find((item) => item.id === key))
    .filter(Boolean)
}

function getSelectedVideoIds () {
  const rows = getSelectedVideoRows()
  const videoIds = rows.map((item) => item.id).filter((id) => id != null && id !== '')
  return { rows, videoIds }
}

function handleTableChange (p) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  getList()
}

function showDrawer (record) {
  visible.value = true
  data.value = record
  selectedRowKeys.value = []
  selectedRows.value = []
  getList()
}

function getList () {
  isLoading.value = true
  const params = {
    project_drama_id: data.value.id,
    page: pagination.current,
    page_size: pagination.pageSize
  }
  getDramaSeriesList(params).then(res => {
    isLoading.value = false
    const { code, msg, data: resData } = res
    if (code === 0) {
      dataSource.value = resData.data
      pagination.total = resData.total
      selectedRowKeys.value = []
      selectedRows.value = []
    } else {
      message.error(res.message || msg)
    }
  }).catch(() => {
    isLoading.value = false
    message.error('获取剧集列表失败')
  })
}

function onClose () {
  if (!isLoading.value && !isSyncAllLoading.value) {
    pagination.current = 1
    pagination.pageSize = 10
    selectedRowKeys.value = []
    selectedRows.value = []
    visible.value = false
  }
}

function touchPreview (record) {
  RefPreviewModal.value.showModal(record.drama_id, record.eq_number)
}

function touchIntroduction (record) {
  RefIntroduction.value.showDrawer(record, data.value.id)
}

// 同步管理中心剧集
function touchSyncEqVideo () {
  if (!data.value?.id) {
    message.error('剧目信息异常')
    return
  }
  isSyncAllLoading.value = true
  syncEqVideo({ project_drama_id: data.value.id })
    .then((res) => {
      if (res.code === 0) {
        message.success('同步成功')
        getList()
      } else {
        message.error(res.message || '同步失败')
      }
      isSyncAllLoading.value = false
    })
    .catch(() => {
      message.error('同步失败')
      isSyncAllLoading.value = false
    })
}

// 微小刷新审核状态
function touchWxMediaAuditStatus () {
  if (!data.value?.id) {
    message.error('剧目信息异常')
    return
  }
  isSyncAllLoading.value = true
  wxMediaAuditStatus({ project_drama_id: data.value.id })
    .then((res) => {
      if (res.code === 0) {
        message.success('刷新成功')
        getList()
      } else {
        message.error(res.message || '刷新失败')
      }
      isSyncAllLoading.value = false
    })
    .catch(() => {
      message.error('刷新失败')
      isSyncAllLoading.value = false
    })
}

// 微信刷新上传状态
function touchWxGetVideoStatus () {
  if (!data.value?.id) {
    message.error('剧目信息异常')
    return
  }
  isSyncAllLoading.value = true
  wxGetVideoStatus({ project_drama_id: data.value.id })
    .then((res) => {
      if (res.code === 0) {
        message.success('同步成功')
        getList()
      } else {
        message.error(res.message || '同步失败')
      }
      isSyncAllLoading.value = false
    })
    .catch(() => {
      message.error('同步失败')
      isSyncAllLoading.value = false
    })
}

// 同步至微小（批量）
function touchWxVideoUploadBatch () {
  if (!selectedRowKeys.value.length) {
    message.error('未选中项目')
    return
  }
  const { rows, videoIds } = getSelectedVideoIds()
  if (!videoIds.length || videoIds.length !== rows.length) {
    message.error('有部分剧集尚未上传视频')
    return
  }
  isSyncAllLoading.value = true
  wxVideoUpload({
    project_drama_id: data.value.id,
    video_ids: videoIds
  })
    .then((res) => {
      if (res.code === 0) {
        message.success('同步成功')
        getList()
      } else {
        message.error(res.message || '同步失败')
      }
      isSyncAllLoading.value = false
    })
    .catch(() => {
      message.error('同步失败')
      isSyncAllLoading.value = false
    })
}

// 同步至微小（单集）
function touchSyncWx (record) {
  if (!record?.id) {
    message.error('剧集信息异常')
    return
  }
  isSyncAllLoading.value = true
  wxVideoUpload({
    project_drama_id: data.value.id,
    video_ids: [record.id]
  })
    .then((res) => {
      if (res.code === 0) {
        message.success('同步成功')
        getList()
      } else {
        message.error(res.message || '同步失败')
      }
      isSyncAllLoading.value = false
    })
    .catch(() => {
      message.error('同步失败')
      isSyncAllLoading.value = false
    })
}

defineExpose({
  showDrawer
})
</script>

<style lang="less" scoped>
.tool-view {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.checkbox-btn {
  margin-left: 10px;
}
.ant-btn-link {
  padding: 0;
  margin-left: 8px;
}
</style>
